import React, { useEffect, useMemo, useState } from "react";
import { useTable, useSortBy, Column } from "react-table";
import { FunnelTableData, FunnelTableRow, ValueCell } from "../../../types";

interface FunnelTableProps {
  columns: Column<any>[];
  data: any[];
}

const FunnelTable: React.FC<FunnelTableProps> = ({ columns, data }) => {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({ columns, data }, useSortBy);

  const [tableGridTemplate, setTableGridTemplate] = useState("1fr");

  useEffect(() => {
    if (data.length === 0) return;

    // Получаем максимальное количество значений в строках
    const maxValuesCount = Math.max(...data.map((row) => row.values.length));

    // Формируем шаблон `gridTemplateColumns`
    const newGridTemplate = `4fr ${maxValuesCount > 9 ? `repeat(${maxValuesCount * 2}, 1fr)` : Array(maxValuesCount)
      .fill("3fr 1fr")
      .join(" ")}`;

    setTableGridTemplate(newGridTemplate);
  }, [data]);

  return (
    <div
      id={`${Math.round(Math.random() * 1000)}`}
      {...getTableProps()}
      style={{ display: "grid", gap: "10px" }}
    >
      {/* Заголовок таблицы */}
      <div
        className="tableHeading"
        style={{
          display: "grid",
          gridTemplateColumns: tableGridTemplate,
        }}
      >
        {headerGroups.map((headerGroup) =>
          headerGroup.headers.map((column, columnIndex) => (
            <div
              {...column.getHeaderProps(column.getSortByToggleProps())}
              style={{
                cursor: column.canSort ? "pointer" : "default",
                textAlign: columnIndex === 0 ? "left" : "center", // Выравнивание заголовков
              }}
            >
              {column.render("Header")}
            </div>
          ))
        )}
      </div>

      {/* Тело таблицы */}
      <div {...getTableBodyProps()} style={{ display: "grid", gap: "10px" }}>
        {rows.map((row, index) => {
          prepareRow(row);

          return (
            <div
              key={`row-${index}`}
              {...row.getRowProps()}
              style={{
                display: "grid",
                gridTemplateColumns: tableGridTemplate,
                padding: "10px 0",
                borderBottom: "1px solid #ccc",
              }}
            >
              {row.cells.map((cell: any, cellIndex) => (
                <div
                  key={`cell-${index}-${cellIndex}`}
                  {...cell.getCellProps()}
                  style={{
                    textAlign: cellIndex === 0 ? "left" : "center", // Выравнивание ячеек
                  }}
                >
                  {cell.render("Cell")}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FunnelTableWrapper: React.FC<FunnelTableData> = (
  props: FunnelTableData
) => {
  const { data } = props;

  // Преобразуем данные и вычисляем конверсию
  const transformedData = useMemo(() => {
    return data.map((row) => {
      const valuesWithConversion = row.values.map((cell, index, array) => {
        if (index === 0) {
          // Для первой ячейки не добавляем конверсию
          return { ...cell };
        }

        const previousValue = array[index - 1].value;
        const conversion =
          previousValue > 0
            ? `${((cell.value / previousValue) * 100).toFixed(2)}%`
            : "-";

        return {
          ...cell,
          conversion,
        };
      });

      return {
        heading: row.heading,
        values: valuesWithConversion,
      };
    });
  }, [data]);

  // Определяем колонки без дублирования колонки "%"
  const columns = useMemo<Column<any>[]>(() => {
    if (transformedData.length === 0) return [{ Header: "Офис", accessor: "heading" }];
  
    const addedHeadings = new Set<string>();
    let isFirstHeading = true;
    let colIndex = 0;
  
    return [
      { Header: "Офис", id: "heading", accessor: "heading", canSort: true },
      ...transformedData[0].values.flatMap((cell: ValueCell, index: number) => {
        const valueColumn = {
          Header: cell.heading,
          accessor: (row: FunnelTableRow) =>
            row.values.find((v: ValueCell) => v.heading === cell.heading)?.value ?? "-",
          canSort: true,
        };
  
        if (isFirstHeading) {
          addedHeadings.add(cell.heading);
          isFirstHeading = false;
          return [valueColumn]; // Первая колонка значений — только она
        }
  
        // Для всех следующих колонок добавляем и колонку значения, и колонку конверсии
        if (addedHeadings.has(cell.heading)) return [valueColumn];
  
        addedHeadings.add(cell.heading);
  
        const conversionColumn = {
          Header: "%",
          id: `conversion_column_${index}_${cell.heading}`,
          accessor: ((fixedCell) => (row: FunnelTableRow) => { // FunnelTableRow
            const currentIndex = row.values.findIndex(v => v.heading === fixedCell.heading);
            const current = row.values[currentIndex]?.value;
            const base = row.values[0]?.value;

            if (!current) {
              return "-";
            }
            const percent = (current / base) * 100;

            return `${percent.toFixed(1)}%`;
          })(cell), // Immediately invoke the outer function with current cell,
          canSort: true
        };
  
        return [conversionColumn, valueColumn]; // Сначала колонка с %, потом значение
      }),
    ];
  }, [transformedData]);

  console.log("Data for table:", transformedData);

  return (
    <div className="tableContainer">
      <FunnelTable key={columns.length} data={transformedData} columns={columns} />
    </div>
  );
};

export default FunnelTableWrapper;